import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  BarChart3, 
  Eye, 
  MousePointerClick, 
  TrendingUp, 
  Globe, 
  Users,
  RefreshCw,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../../../App';

const WidgetAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/widget-analytics/summary?days=${period}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      // Don't show error toast on first load - might just be no data
      if (analytics) {
        toast.error('Failed to refresh analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAnalytics();
    setRefreshing(false);
    toast.success('Analytics refreshed');
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'teal' }) => (
    <Card className="dark:bg-slate-800">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 dark:text-slate-400">{title}</p>
            <p className={`text-3xl font-bold text-${color}-600`}>{value}</p>
            {subtitle && (
              <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`p-3 bg-${color}-100 dark:bg-${color}-900/30 rounded-full`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  // Handle case where there's no data yet
  const hasData = analytics?.totals?.views > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-teal-600" />
            Widget Analytics
          </h2>
          <p className="text-slate-500 dark:text-slate-400">
            Track your embed widget performance across external websites
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {!hasData ? (
        // Empty state
        <Card className="dark:bg-slate-800">
          <CardContent className="py-16 text-center">
            <BarChart3 className="h-16 w-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              No analytics data yet
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
              Analytics will appear here once your embed widget starts receiving views. 
              Make sure you've added the widget to your website.
            </p>
            <Button
              onClick={() => window.location.href = '/admin/settings'}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Get Embed Code
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Total Views"
              value={analytics.totals.views.toLocaleString()}
              subtitle={`${period} day period`}
              icon={Eye}
              color="blue"
            />
            <StatCard
              title="Button Clicks"
              value={analytics.totals.clicks.toLocaleString()}
              subtitle={`${analytics.totals.click_rate}% click rate`}
              icon={MousePointerClick}
              color="purple"
            />
            <StatCard
              title="Conversions"
              value={analytics.totals.conversions.toLocaleString()}
              subtitle={`${analytics.totals.conversion_rate}% conversion`}
              icon={Users}
              color="green"
            />
            <StatCard
              title="Click Rate"
              value={`${analytics.totals.click_rate}%`}
              subtitle="Views → Clicks"
              icon={TrendingUp}
              color="orange"
            />
          </div>

          {/* Charts & Tables Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Trend */}
            <Card className="dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="text-lg">Daily Performance</CardTitle>
                <CardDescription>Views and clicks over time</CardDescription>
              </CardHeader>
              <CardContent>
                {analytics.daily?.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.daily.slice(-10).map((day, index) => {
                      const maxViews = Math.max(...analytics.daily.map(d => d.views || 1));
                      const viewWidth = (day.views / maxViews) * 100;
                      const clickWidth = (day.clicks / maxViews) * 100;
                      
                      return (
                        <div key={index} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600 dark:text-slate-400">
                              {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                            <span className="text-slate-900 dark:text-white font-medium">
                              {day.views} views · {day.clicks} clicks
                            </span>
                          </div>
                          <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden flex gap-0.5">
                            <div 
                              className="h-full bg-blue-500 rounded-full transition-all"
                              style={{ width: `${viewWidth}%` }}
                            />
                            <div 
                              className="h-full bg-purple-500 rounded-full transition-all"
                              style={{ width: `${clickWidth}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                    <div className="flex items-center gap-4 pt-2 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-blue-500 rounded" /> Views
                      </span>
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-purple-500 rounded" /> Clicks
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No daily data available</p>
                )}
              </CardContent>
            </Card>

            {/* Top Referrers */}
            <Card className="dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Globe className="h-5 w-5 text-teal-600" />
                  Top Referrers
                </CardTitle>
                <CardDescription>Websites embedding your widget</CardDescription>
              </CardHeader>
              <CardContent>
                {analytics.top_referrers?.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.top_referrers.map((referrer, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold text-slate-400">#{index + 1}</span>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white truncate max-w-[200px]">
                              {referrer.domain || 'Direct'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-slate-900 dark:text-white">
                            {referrer.views} <span className="text-slate-400 text-sm">views</span>
                          </p>
                          <p className="text-sm text-purple-600">
                            {referrer.clicks} clicks
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No referrer data available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Plans Performance */}
          {analytics.top_plans?.length > 0 && (
            <Card className="dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="text-lg">Plan Performance</CardTitle>
                <CardDescription>Which plans are getting the most attention</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {analytics.top_plans.map((plan, index) => (
                    <div key={index} className="p-4 border rounded-lg dark:border-slate-700">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-slate-900 dark:text-white">
                          {plan.plan_name}
                        </h4>
                        {index === 0 && (
                          <Badge className="bg-yellow-100 text-yellow-700">Top</Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div>
                          <p className="text-2xl font-bold text-blue-600">{plan.views}</p>
                          <p className="text-xs text-slate-500">views</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-purple-600">{plan.clicks}</p>
                          <p className="text-xs text-slate-500">clicks</p>
                        </div>
                      </div>
                      {plan.views > 0 && (
                        <div className="mt-3 pt-3 border-t dark:border-slate-700">
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            {((plan.clicks / plan.views) * 100).toFixed(1)}% click rate
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Language Distribution */}
          {analytics.languages && Object.keys(analytics.languages).length > 0 && (
            <Card className="dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="text-lg">Language Preference</CardTitle>
                <CardDescription>Widget language selection by visitors</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  {Object.entries(analytics.languages).map(([lang, count]) => (
                    <div key={lang} className="flex items-center gap-2">
                      <Badge variant="outline" className="text-lg px-3 py-1">
                        {lang === 'en' ? '🇺🇸 EN' : lang === 'es' ? '🇪🇸 ES' : lang.toUpperCase()}
                      </Badge>
                      <span className="text-slate-600 dark:text-slate-400">
                        {count.toLocaleString()} ({((count / analytics.totals.views) * 100).toFixed(0)}%)
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default WidgetAnalytics;
